import React, { useCallback, useEffect, useRef, useState } from "react";

import { useLicense } from "../../../LicenseContext";
import { usePermission } from "../../../UserContext";
import { useNotify } from "../../../ToastContext";
import { CONTROLSEAT_URL } from "../../../api";
import xmarkIcon from "../../../assets/xmark.svg";
import {
  getLicenseUrgency,
  urgencyLabel,
} from "../../../utils/licenseUrgency";

const STATUS_PILL_LABEL = {
  licensed: "Licensed",
  expired: "Expired",
  unlicensed: "Unlicensed",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  const onClick = () => {
    if (!value || !navigator.clipboard) return;
    navigator.clipboard.writeText(value).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {}
    );
  };
  return (
    <button type="button" className="gwLicenseCopyBtn" onClick={onClick}>
      {copied ? "Copied" : label || "Copy"}
    </button>
  );
}

// downloadActivationRequest produces a small JSON file the customer can
// email to support@controlseat.com (or upload to the customer portal once
// it exists). The file embeds the install_id plus enough audit signal to
// detect tampering ("did the customer hand us their actual gateway's id,
// or someone else's?" — we cross-check with the hostname).
function downloadActivationRequest({ installId, fingerprint, hostname }) {
  const payload = {
    install_id: installId,
    host_fingerprint: fingerprint || "",
    hostname: hostname || "",
    requested_at: new Date().toISOString(),
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `activation-request-${(installId || "gateway").slice(0, 8)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function ActivationHero({ gatewayInstallId, gatewayFingerprint, onActivateOnline, onNotify, activating }) {
  const [onlineError, setOnlineError] = useState(null);
  const [onlineActivating, setOnlineActivating] = useState(false);
  const busy = activating || onlineActivating;

  const handleOnline = useCallback(async () => {
    if (busy) return;
    setOnlineError(null);
    setOnlineActivating(true);
    try {
      await onActivateOnline();
      onNotify?.({
        type: "success",
        title: "License activated",
        message: "The new license is active.",
      });
    } catch (err) {
      // Surface inline (persists next to the button) AND as a toast, to
      // match the gateway-wide "every mutation emits feedback" convention.
      const msg = err?.message || "Online activation failed";
      setOnlineError(msg);
      onNotify?.({ type: "error", title: "Activation failed", message: msg });
    } finally {
      setOnlineActivating(false);
    }
  }, [busy, onActivateOnline, onNotify]);

  if (!gatewayInstallId) {
    return (
      <div className="gwLicenseActivation">
        <div className="gwLicenseActivationTitle">Activation needed</div>
        <div className="gwLicenseActivationBody">
          The gateway is starting up — install_id will appear here shortly.
          Refresh in a few seconds.
        </div>
      </div>
    );
  }

  return (
    <div className="gwLicenseActivation">
      <div className="gwLicenseActivationTitle">Activation needed</div>
      <div className="gwLicenseActivationBody">
        Click <strong>Activate online</strong> to sign in to the Control
        Seat licensing portal and have a license issued automatically.
        You can also visit{" "}
        <a
          href={`${CONTROLSEAT_URL}/licenses`}
          target="_blank"
          rel="noreferrer"
        >
          {`${new URL(CONTROLSEAT_URL).host}/licenses`}
        </a>{" "}
        to generate one manually and drop the file below. Each license is
        bound to this gateway, so the id below is your activation key.
      </div>
      <div className="gwLicenseActivationIdRow">
        <code className="gwLicenseActivationId">{gatewayInstallId}</code>
        <CopyButton value={gatewayInstallId} label="Copy install_id" />
        <button
          type="button"
          className="gwLicenseCopyBtn"
          onClick={() =>
            downloadActivationRequest({
              installId: gatewayInstallId,
              fingerprint: gatewayFingerprint,
              hostname:
                typeof window !== "undefined" ? window.location.hostname : "",
            })
          }
        >
          Download request
        </button>
      </div>
      {gatewayFingerprint ? (
        <div className="gwLicenseActivationIdRow">
          <code
            className="gwLicenseActivationId"
            title="Host fingerprint — SHA256 of this gateway's machine identifiers. Bound into the license at sign time."
          >
            {gatewayFingerprint.slice(0, 16)}…{gatewayFingerprint.slice(-8)}
          </code>
          <CopyButton value={gatewayFingerprint} label="Copy fingerprint" />
          <span className="gwLicenseActivationHint">
            Hardware fingerprint — bound into your license alongside the
            install_id.
          </span>
        </div>
      ) : (
        <div className="gwLicenseActivationIdRow">
          <span className="gwLicenseActivationHint">
            Host fingerprint unavailable — license will use install_id binding
            only. Mount <code>/etc/machine-id</code> into the gateway container
            to enable hardware binding.
          </span>
        </div>
      )}
      <div className="gwLicenseActivationActions">
        <button
          type="button"
          className="gwPrimaryBtn"
          onClick={handleOnline}
          disabled={busy}
        >
          {busy ? "Activating…" : "Activate online"}
        </button>
        <span className="gwLicenseActivationHint">
          Opens a popup to controlseat.com. The license is delivered straight
          back to this page — no copy-paste.
        </span>
      </div>
      {onlineError && (
        <div className="gwLicenseUploadError">{onlineError}</div>
      )}
    </div>
  );
}

function CountdownCard({ mode, daysRemaining, urgency }) {
  if (mode === "unlicensed") {
    return (
      <div className={`gwLicenseCountdown is-${urgency}`}>
        <span className="gwLicenseCountdownLabel">
          No license loaded — drop a license.json below to activate.
        </span>
      </div>
    );
  }
  if (mode === "expired" || (typeof daysRemaining === "number" && daysRemaining <= 0)) {
    const expiredFor = Math.abs(daysRemaining || 0);
    // Without a positive day count (daysRemaining 0/missing, or mode expired
    // with no negative count) show the same "License expired" wording
    // urgencyLabel uses — not a confusing "0 days past expiry".
    if (expiredFor === 0) {
      return (
        <div className={`gwLicenseCountdown is-${urgency}`}>
          <span className="gwLicenseCountdownLabel">License expired</span>
        </div>
      );
    }
    return (
      <div className={`gwLicenseCountdown is-${urgency}`}>
        <span className="gwLicenseCountdownNumber">{expiredFor}</span>
        <span className="gwLicenseCountdownLabel">
          {expiredFor === 1 ? "day past expiry" : "days past expiry"}
        </span>
      </div>
    );
  }
  return (
    <div className={`gwLicenseCountdown is-${urgency}`}>
      <span className="gwLicenseCountdownNumber">{daysRemaining}</span>
      <span className="gwLicenseCountdownLabel">
        {daysRemaining === 1 ? "day remaining" : "days remaining"}
      </span>
    </div>
  );
}

// LicenseInput accepts a license.json EITHER as a file (drag-drop /
// browse) OR as raw JSON text in a textarea (paste path). Two modes,
// one form. Mirrors the TLS page pattern. Submitting with paste-mode
// converts the trimmed text to a File so the multipart upload path
// stays a single code path.
function LicenseInput({ mode, onMode, onFile, onText, text, uploading }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragging(false);
      if (uploading) return;
      const file = e.dataTransfer?.files?.[0];
      if (file) onFile(file);
    },
    [onFile, uploading]
  );

  return (
    <div className="gwTlsInputBlock">
      <div className="gwTlsInputHeader">
        <div className="gwTlsFileLabel">License file</div>
        <div className="gwTlsModeTabs" role="tablist" aria-label="License input mode">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "file"}
            className={`gwTlsModeTab ${mode === "file" ? "is-active" : ""}`}
            onClick={() => onMode("file")}
          >
            Upload file
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "paste"}
            className={`gwTlsModeTab ${mode === "paste" ? "is-active" : ""}`}
            onClick={() => onMode("paste")}
          >
            Paste content
          </button>
        </div>
      </div>

      {mode === "file" ? (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="application/json,.json"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
              e.target.value = "";
            }}
          />
          <div
            className={`gwLicenseDropZone ${dragging ? "is-dragging" : ""} ${
              uploading ? "is-uploading" : ""
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              if (!uploading) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => !uploading && inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !uploading) {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
          >
            <div className="gwLicenseDropZoneTitle">
              {uploading ? "Uploading…" : "Drop a license.json file here"}
            </div>
            <div className="gwLicenseDropZoneHint">
              or click to browse. The signature is verified before the file
              touches disk.
            </div>
          </div>
        </>
      ) : (
        <textarea
          className="gwTlsPasteArea"
          name="license"
          placeholder='{ "customer": "...", "install_id": "...", "issued_at": "...", "expires_at": "...", "signature": "..." }'
          spellCheck={false}
          autoComplete="off"
          value={text}
          onChange={(e) => onText(e.target.value)}
          rows={10}
          disabled={uploading}
        />
      )}
    </div>
  );
}

function licenseTextToFile(text) {
  const trimmed = (text || "").trim();
  if (!trimmed) return null;
  return new File([trimmed], "license.json", { type: "application/json" });
}

// The backend appends a "host=… fingerprint=…" suffix to every audit
// reason (core/license hostSignal) and stamps removals with the admin's
// raw user id. Neither is useful in the UI — the host/fingerprint is
// identical on every row for a single-gateway install and the id is
// opaque — so strip them, keeping the meaningful part of the reason.
function cleanAuditReason(reason) {
  if (!reason) return "";
  return reason
    .replace(/\s*host=\S+(?:\s+fingerprint=\S+)?\s*$/, "")
    .replace(/removed by admin \S+/i, "removed by an administrator")
    .trim();
}

function AuditRow({ entry }) {
  const eventClass =
    entry.event_type === "rejected"
      ? "is-rejected"
      : entry.event_type === "uploaded"
      ? "is-uploaded"
      : "";
  // Routine "loaded" events (one per restart/reload) are de-emphasized so
  // the state-changes — uploaded / rejected / removed — stand out.
  const isRoutine = entry.event_type === "loaded";
  const details = [
    entry.customer || "(no customer)",
    entry.edition || "",
    entry.expires_at ? `expires ${formatDate(entry.expires_at)}` : "",
    cleanAuditReason(entry.reason),
  ].filter(Boolean);
  // created_by is the uploader's Cognito sub — surface an email if one came
  // through, otherwise a generic label rather than the raw id.
  const uploadedBy =
    entry.event_type === "uploaded"
      ? entry.created_by && entry.created_by.includes("@")
        ? `Uploaded by ${entry.created_by}`
        : "Uploaded by an administrator"
      : "";
  return (
    <div className={`gwLicenseAuditRow${isRoutine ? " is-routine" : ""}`}>
      <span className="gwLicenseAuditTime">
        {formatDateTime(entry.created_at)}
      </span>
      <span className="gwLicenseAuditDetail">
        {uploadedBy ? `${uploadedBy} · ` : ""}
        {details.join(" · ")}
      </span>
      <span className={`gwLicenseAuditEvent ${eventClass}`}>
        {entry.event_type}
      </span>
    </div>
  );
}

export function PlatformLicensePage() {
  const license = useLicense();
  const isAdmin = usePermission("admin");
  const notify = useNotify();
  const {
    uploadLicense,
    removeLicense,
    uploading,
    uploadError,
    activateOnline,
  } = license;

  const [inputMode, setInputMode] = useState("file");
  const [pasteText, setPasteText] = useState("");
  const [removing, setRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  // Esc closes the confirm modal — matches the gateway-wide modal
  // convention (alarm management, projects, etc.).
  useEffect(() => {
    if (!confirmRemove) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setConfirmRemove(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmRemove]);

  const submit = useCallback(
    async (file) => {
      try {
        await uploadLicense(file);
        if (inputMode === "paste") setPasteText("");
        notify?.({ type: "success", title: "License updated", message: "The new license is active." });
      } catch (err) {
        notify?.({ type: "error", title: "Upload failed", message: err?.message || "Upload failed." });
      }
    },
    [uploadLicense, notify, inputMode]
  );

  const onPasteSubmit = useCallback(() => {
    const file = licenseTextToFile(pasteText);
    if (!file) return;
    submit(file);
  }, [pasteText, submit]);

  const confirmAndRemove = useCallback(async () => {
    setRemoving(true);
    try {
      await removeLicense();
      setConfirmRemove(false);
      notify?.({
        type: "success",
        title: "License removed",
        message: "The gateway is now unlicensed. Upload a license to resume runtime traffic.",
      });
    } catch (err) {
      notify?.({
        type: "error",
        title: "Remove failed",
        message: err?.message || "Failed to remove license.",
      });
    } finally {
      setRemoving(false);
    }
  }, [removeLicense, notify]);

  if (license.loading) {
    return (
      <div className="gwContentGrid">
        <section className="gwFormStack">
          <div className="gwPageHeader">
            <div>
              <div className="gwPageTitle">License</div>
              <div className="gwPageSubtitle">Loading…</div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const urgency = getLicenseUrgency(license);
  const statusKey = license.mode || "unlicensed";
  const pillLabel = STATUS_PILL_LABEL[statusKey] || "Unknown";
  const needsActivation =
    license.mode === "unlicensed" || license.mode === "expired";

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">License</div>
            <div className="gwPageSubtitle">{urgencyLabel(license)}</div>
          </div>
        </div>

        {needsActivation && isAdmin && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Activation</div>
            <div className="gwCardSectionBody">
              <ActivationHero
                gatewayInstallId={license.gatewayInstallId}
                gatewayFingerprint={license.gatewayFingerprint}
                onActivateOnline={activateOnline}
                onNotify={notify}
                activating={uploading}
              />
            </div>
          </div>
        )}

        <div className="gwCardSection">
          <div className="gwSectionTitle">Status</div>
          <div className="gwCardSectionBody">
            <div className="gwLicenseHero">
              <span className={`gwLicenseStatusPill is-${urgency}`}>
                {pillLabel}
              </span>
              <span className="gwLicenseHeroName">
                {license.customer || "No license loaded"}
              </span>
              {license.edition && (
                <span className="gwLicenseEditionBadge">{license.edition}</span>
              )}
            </div>

            <CountdownCard
              mode={license.mode}
              daysRemaining={license.daysRemaining}
              urgency={urgency}
            />

            <div className="gwLicenseDetailsGrid">
              <div className="gwLicenseDetail">
                <span className="gwLicenseDetailLabel">Gateway install ID</span>
                <span className="gwLicenseDetailValue">
                  {license.gatewayInstallId || "—"}
                  {license.gatewayInstallId && (
                    <CopyButton value={license.gatewayInstallId} />
                  )}
                </span>
              </div>
              <div className="gwLicenseDetail">
                <span className="gwLicenseDetailLabel">License install ID</span>
                <span className="gwLicenseDetailValue">
                  {license.installId || "—"}
                  {license.installId && <CopyButton value={license.installId} />}
                </span>
              </div>
              <div className="gwLicenseDetail">
                <span
                  className="gwLicenseDetailLabel"
                  title="SHA256 of this gateway's machine identifiers (machine-id, DMI UUID, MAC). Bound into your license at sign time — cloning the database to a different host yields a different hash and the license stops working."
                >
                  Host fingerprint
                </span>
                <span className="gwLicenseDetailValue">
                  {license.gatewayFingerprint
                    ? `${license.gatewayFingerprint.slice(0, 16)}…${license.gatewayFingerprint.slice(-8)}`
                    : "—"}
                  {license.gatewayFingerprint && (
                    <CopyButton value={license.gatewayFingerprint} />
                  )}
                </span>
              </div>
              <div className="gwLicenseDetail">
                <span className="gwLicenseDetailLabel">License fingerprint</span>
                <span className="gwLicenseDetailValue">
                  {license.hostFingerprint
                    ? `${license.hostFingerprint.slice(0, 16)}…${license.hostFingerprint.slice(-8)}`
                    : "(none)"}
                  {license.hostFingerprint && (
                    <CopyButton value={license.hostFingerprint} />
                  )}
                </span>
              </div>
              <div className="gwLicenseDetail">
                <span className="gwLicenseDetailLabel">Issued at</span>
                <span className="gwLicenseDetailValue is-text">
                  {formatDate(license.issuedAt)}
                </span>
              </div>
              <div className="gwLicenseDetail">
                <span className="gwLicenseDetailLabel">Expires at</span>
                <span className="gwLicenseDetailValue is-text">
                  {formatDate(license.expiresAt)}
                </span>
              </div>
              <div className="gwLicenseDetail">
                <span className="gwLicenseDetailLabel">Last loaded</span>
                <span className="gwLicenseDetailValue is-text">
                  {formatDateTime(license.lastLoadedAt)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Replace license</div>
            <div className="gwCardSectionBody">
              <LicenseInput
                mode={inputMode}
                onMode={setInputMode}
                onFile={submit}
                onText={setPasteText}
                text={pasteText}
                uploading={uploading}
              />
              {inputMode === "paste" && (
                <div className="gwTlsFormActions">
                  <button
                    type="button"
                    className="gwPrimaryBtn"
                    disabled={uploading || !pasteText.trim()}
                    onClick={onPasteSubmit}
                  >
                    {uploading ? "Validating + saving…" : "Upload pasted license"}
                  </button>
                </div>
              )}
              {uploadError && (
                <div className="gwAlert">
                  <div className="gwAlertTitle">Upload failed</div>
                  <div className="gwAlertBody">{uploadError}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {isAdmin && license.mode && license.mode !== "unlicensed" && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Danger zone</div>
            <div className="gwCardSectionBody">
              <div className="gwLicenseNote">
                Removing the license deletes <code>license.json</code>{" "}
                from disk and immediately stops live tag data, historian
                queries, and runtime page rendering. Reversible — upload
                the file again to resume. Useful for testing or before
                handing the gateway off to a different customer.
              </div>
              <div className="gwTlsFormActions">
                <button
                  type="button"
                  className="gwDangerBtn"
                  disabled={removing || uploading}
                  onClick={() => setConfirmRemove(true)}
                >
                  Remove license
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmRemove && (
          <div
            className="gwOverlay"
            onClick={() => !removing && setConfirmRemove(false)}
          >
            <div
              className="gwModal"
              onClick={(e) => e.stopPropagation()}
              style={{ width: 460 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="gw-remove-license-title"
            >
              <div className="gwModalHeader">
                <div>
                  <div className="gwModalTitle" id="gw-remove-license-title">
                    Remove license
                  </div>
                  <div className="gwModalSubtitle">
                    {license.customer || "(no customer)"} · expires{" "}
                    {formatDate(license.expiresAt)}
                  </div>
                </div>
                <button
                  className="gwModalClose"
                  type="button"
                  onClick={() => !removing && setConfirmRemove(false)}
                  aria-label="Close"
                >
                  <img
                    src={xmarkIcon}
                    alt=""
                    className="gwIcon"
                    style={{ width: 10, height: 10, opacity: 0.7 }}
                  />
                </button>
              </div>
              <div className="gwModalBody">
                <p
                  style={{
                    fontSize: 13,
                    color: "var(--gw-text-secondary)",
                    margin: 0,
                  }}
                >
                  This deletes <code>license.json</code> from disk and
                  swaps the gateway to an unlicensed state immediately.
                  Live tag data, historian queries, and runtime page
                  rendering will stop. Reversible — re-upload a valid
                  license to resume.
                </p>
              </div>
              <div className="gwModalFooter" style={{ justifyContent: "flex-end" }}>
                <button
                  className="gwGhostBtn"
                  type="button"
                  onClick={() => setConfirmRemove(false)}
                  disabled={removing}
                >
                  Cancel
                </button>
                <button
                  className="gwDangerBtn"
                  type="button"
                  onClick={confirmAndRemove}
                  disabled={removing}
                >
                  {removing ? "Removing…" : "Remove license"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">License history</div>
            <div className="gwCardSectionBody">
              {license.recentAudit && license.recentAudit.length > 0 ? (
                <div className="gwLicenseAuditList">
                  {license.recentAudit.map((entry) => (
                    <AuditRow key={entry.id} entry={entry} />
                  ))}
                </div>
              ) : (
                <div className="gwEmptyState">
                  <div className="gwEmptyTitle">No recent license events</div>
                  <div className="gwEmptyBody">
                    Audit entries appear here after the gateway loads or replaces a license.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default PlatformLicensePage;
